// src/screens/RotationManager.tsx
// Gestion live des rotations : chrono, 5 sur le terrain, banc, changements,
// temps de jeu par joueur / par période. Sauvegarde locale automatique
// (localStorage) + sauvegarde manuelle dans matches.rotations (jsonb).
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation as useTranslationOrig } from "react-i18next";
import {
  ArrowLeft,
  ArrowLeftRight,
  ArrowRight,
  BarChart2,
  Check,
  ChevronLeft,
  ChevronRight,
  Minus,
  Pause,
  Play,
  Plus,
  Redo2,
  Save,
  Settings2,
  Undo2,
  Users,
  X,
} from "lucide-react";
import { supabase } from "../supabaseClient";
import {
  COURT_SIZE,
  emptyState,
  formatClock,
  formatPlay,
  isValidState,
  mergeByNumber,
  normalizePlayers,
  parseClock,
  periodDurationMs,
  RotationState,
  RPlayer,
  Settings,
  Snapshot,
  storageKey,
  totalPlay,
} from "../lib/rotations";
import { Card, IconBtn, Modal, SquadModal, TR } from "../components/rotations/RotationUI";

type RawT = (key: string, options?: Record<string, unknown>) => any;
const useTranslationSafe = (ns?: string) =>
  (useTranslationOrig as unknown as (ns?: any) => { t: RawT })(ns);

export const RotationManager = () => {
  const { match_id } = useParams();
  const navigate = useNavigate();
  const { t: tRaw } = useTranslationSafe("rotations");
  const tr = useCallback(
    (key: string, options?: Record<string, unknown>): string => String(tRaw(key, options) ?? ""),
    [tRaw]
  );

  const [loading, setLoading] = useState(true);
  const [state, setState] = useState<RotationState | null>(null);
  const [teamRoster, setTeamRoster] = useState<RPlayer[]>([]);
  const [opponent, setOpponent] = useState<string>("");
  const [running, setRunning] = useState(false);
  const [tab, setTab] = useState<"subs" | "analysis">("subs");
  const [logPeriod, setLogPeriod] = useState(1);
  const [selectedOutSlot, setSelectedOutSlot] = useState<number | null>(null);
  const [selectedIn, setSelectedIn] = useState<number | null>(null);
  const [past, setPast] = useState<Snapshot[]>([]);
  const [future, setFuture] = useState<Snapshot[]>([]);
  const [showSquad, setShowSquad] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [editingClock, setEditingClock] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");

  const lastTickRef = useRef<number | null>(null);

  // ---------- Chargement ----------
  useEffect(() => {
    if (!match_id) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      let local: RotationState | null = null;
      try {
        const raw = localStorage.getItem(storageKey(match_id));
        if (raw) {
          const parsed = JSON.parse(raw);
          if (isValidState(parsed)) local = parsed;
        }
      } catch {
        /* stockage indisponible */
      }

      const { data: match, error } = await supabase
        .from("matches")
        .select("*")
        .eq("id", match_id)
        .single();

      if (cancelled) return;

      if (error || !match) {
        console.error("RotationManager fetch match error:", error);
        setState(local ?? emptyState([]));
        setLoading(false);
        return;
      }

      setOpponent(match.opponent ?? "");
      const matchPlayers = normalizePlayers(match.players);

      let roster: RPlayer[] = [];
      if (match.team_id) {
        const { data: team } = await supabase
          .from("teams")
          .select("players")
          .eq("id", match.team_id)
          .maybeSingle();
        roster = normalizePlayers(team?.players);
      }
      if (cancelled) return;
      setTeamRoster(mergeByNumber(roster, matchPlayers));

      const remote: RotationState | null = isValidState(match.rotations) ? match.rotations : null;
      const best =
        local && remote ? (local.updatedAt >= remote.updatedAt ? local : remote) : local ?? remote;

      if (best) {
        setState(best);
        setLogPeriod(best.period);
      } else {
        const squad = matchPlayers.length ? matchPlayers : roster;
        setState(emptyState(squad.sort((a, b) => a.number - b.number)));
        // Pas encore de sélection : on ouvre directement le choix des joueurs.
        setShowSquad(true);
      }
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [match_id]);

  // ---------- Sauvegarde locale auto ----------
  useEffect(() => {
    if (!state || !match_id) return;
    const id = window.setTimeout(() => {
      try {
        localStorage.setItem(storageKey(match_id), JSON.stringify(state));
      } catch {
        /* ignore */
      }
    }, running ? 1000 : 150);
    return () => window.clearTimeout(id);
  }, [state, match_id, running]);

  // ---------- Chrono ----------
  useEffect(() => {
    if (!running) {
      lastTickRef.current = null;
      return;
    }
    lastTickRef.current = Date.now();
    const id = window.setInterval(() => {
      const now = Date.now();
      const delta = now - (lastTickRef.current ?? now);
      lastTickRef.current = now;
      setState((prev) => {
        if (!prev) return prev;
        const nextClock = Math.max(0, prev.clockMs - delta);
        const elapsed = prev.clockMs - nextClock;
        if (elapsed <= 0) return prev;
        const playMs = { ...prev.playMs };
        const stintMs = { ...prev.stintMs };
        prev.onCourt.forEach((num) => {
          if (num == null) return;
          const byPeriod = { ...(playMs[num] || {}) };
          byPeriod[prev.period] = (byPeriod[prev.period] || 0) + elapsed;
          playMs[num] = byPeriod;
          stintMs[num] = (stintMs[num] || 0) + elapsed;
        });
        return { ...prev, clockMs: nextClock, playMs, stintMs, updatedAt: Date.now() };
      });
    }, 250);
    return () => window.clearInterval(id);
  }, [running]);

  useEffect(() => {
    if (running && state && state.clockMs <= 0) setRunning(false);
  }, [running, state]);

  // ---------- Helpers ----------
  const playerByNum = useMemo(() => {
    const map = new Map<number, RPlayer>();
    state?.squad.forEach((p) => map.set(p.number, p));
    return map;
  }, [state?.squad]);

  const playerLabel = useCallback(
    (num: number | null) => {
      if (num == null) return "—";
      const p = playerByNum.get(num);
      if (!p) return `#${num}`;
      const name = [p.firstName, p.lastName].filter(Boolean).join(" ");
      return name ? `#${num} ${name}` : `#${num}`;
    },
    [playerByNum]
  );

  const slotLabels = useMemo(
    () => ["pg", "sg", "sf", "pf", "c"].map((k) => tr(`positions.${k}`)),
    [tr]
  );

  const periodLabel = useCallback(
    (p: number) => {
      if (!state) return String(p);
      return p <= state.settings.periods
        ? tr("period.short", { n: p })
        : tr("period.ot", { n: p - state.settings.periods });
    },
    [state, tr]
  );

  const stateRef = useRef(state);
  stateRef.current = state;

  const snapshotOf = (s: RotationState): Snapshot => ({
    onCourt: s.onCourt,
    subs: s.subs,
    starters: s.starters,
    hasStarted: s.hasStarted,
  });

  const commit = (updater: (prev: RotationState) => RotationState) => {
    const cur = stateRef.current;
    if (!cur) return;
    setPast((p) => [...p.slice(-49), snapshotOf(cur)]);
    setFuture([]);
    setState((prev) => (prev ? { ...updater(prev), updatedAt: Date.now() } : prev));
  };

  const clearSelection = () => {
    setSelectedOutSlot(null);
    setSelectedIn(null);
  };

  // Entrée d'un joueur du banc sur un créneau (remplace éventuellement l'occupant)
  const substitute = (slot: number, inNum: number) => {
    commit((prev) => {
      if (prev.onCourt.includes(inNum)) return prev;
      const outNum = prev.onCourt[slot];
      const onCourt = [...prev.onCourt];
      onCourt[slot] = inNum;
      const stintMs = { ...prev.stintMs, [inNum]: 0 };
      const subs = prev.hasStarted
        ? [
            ...prev.subs,
            {
              id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
              period: prev.period,
              clockMs: prev.clockMs,
              inNum,
              outNum,
            },
          ]
        : prev.subs;
      return { ...prev, onCourt, stintMs, subs };
    });
    clearSelection();
  };

  const removeFromCourt = (slot: number) => {
    commit((prev) => {
      const outNum = prev.onCourt[slot];
      if (outNum == null) return prev;
      const onCourt = [...prev.onCourt];
      onCourt[slot] = null;
      const subs = prev.hasStarted
        ? [
            ...prev.subs,
            {
              id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
              period: prev.period,
              clockMs: prev.clockMs,
              inNum: null,
              outNum,
            },
          ]
        : prev.subs;
      return { ...prev, onCourt, subs };
    });
    clearSelection();
  };

  const onCourtClick = (slot: number) => {
    if (!state) return;
    if (selectedIn != null) {
      substitute(slot, selectedIn);
      return;
    }
    if (state.onCourt[slot] == null) return;
    setSelectedOutSlot((cur) => (cur === slot ? null : slot));
  };

  const onBenchClick = (num: number) => {
    if (!state) return;
    if (selectedOutSlot != null) {
      substitute(selectedOutSlot, num);
      return;
    }
    const emptySlot = state.onCourt.findIndex((n) => n == null);
    if (emptySlot !== -1) {
      substitute(emptySlot, num);
      return;
    }
    setSelectedIn((cur) => (cur === num ? null : num));
  };

  const undo = () => {
    if (!past.length || !state) return;
    const snap = past[past.length - 1];
    setPast((p) => p.slice(0, -1));
    setFuture((f) => [...f, snapshotOf(state)]);
    setState((prev) => (prev ? { ...prev, ...snap, updatedAt: Date.now() } : prev));
    clearSelection();
  };

  const redo = () => {
    if (!future.length || !state) return;
    const snap = future[future.length - 1];
    setFuture((f) => f.slice(0, -1));
    setPast((p) => [...p, snapshotOf(state)]);
    setState((prev) => (prev ? { ...prev, ...snap, updatedAt: Date.now() } : prev));
    clearSelection();
  };

  const toggleRunning = () => {
    if (!state) return;
    if (!running) {
      if (state.clockMs <= 0) return;
      if (!state.hasStarted) {
        // Premier coup de sifflet : on fige le 5 de départ.
        setState((prev) =>
          prev ? { ...prev, hasStarted: true, starters: [...prev.onCourt], updatedAt: Date.now() } : prev
        );
      }
    }
    setRunning((r) => !r);
  };

  const adjustClock = (deltaMs: number) => {
    setState((prev) => {
      if (!prev) return prev;
      const max = periodDurationMs(prev.settings, prev.period);
      return { ...prev, clockMs: Math.min(max, Math.max(0, prev.clockMs + deltaMs)), updatedAt: Date.now() };
    });
  };

  const commitClockEdit = () => {
    if (editingClock == null) return;
    const ms = parseClock(editingClock);
    if (ms != null) {
      setState((prev) => {
        if (!prev) return prev;
        const max = periodDurationMs(prev.settings, prev.period);
        return { ...prev, clockMs: Math.min(max, ms), updatedAt: Date.now() };
      });
    }
    setEditingClock(null);
  };

  const changePeriod = (dir: 1 | -1) => {
    if (!state) return;
    const next = state.period + dir;
    if (next < 1) return;
    setRunning(false);
    setState((prev) =>
      prev
        ? { ...prev, period: next, clockMs: periodDurationMs(prev.settings, next), updatedAt: Date.now() }
        : prev
    );
    setLogPeriod(next);
  };

  // ---------- Sauvegarde Supabase ----------
  const saveRemote = async () => {
    if (!state || !match_id) return;
    setSaveStatus("saving");
    const { error } = await supabase
      .from("matches")
      .update({ rotations: { ...state, updatedAt: Date.now() } })
      .eq("id", match_id);
    if (error) {
      console.error("RotationManager save error:", error);
      setSaveStatus("error");
    } else {
      setSaveStatus("saved");
    }
    window.setTimeout(() => setSaveStatus("idle"), 2500);
  };

  // ---------- Sélection des joueurs du match ----------
  const applySquad = (squad: RPlayer[]) => {
    commit((prev) => {
      const keep = new Set(squad.map((p) => p.number));
      return {
        ...prev,
        squad: [...squad].sort((a, b) => a.number - b.number),
        onCourt: prev.onCourt.map((n) => (n != null && keep.has(n) ? n : null)),
      };
    });
    setShowSquad(false);
  };

  const applySettings = (settings: Settings) => {
    setRunning(false);
    setState((prev) => {
      if (!prev) return prev;
      const max = periodDurationMs(settings, prev.period);
      const wasFull = prev.clockMs === periodDurationMs(prev.settings, prev.period);
      return {
        ...prev,
        settings,
        clockMs: wasFull ? max : Math.min(prev.clockMs, max),
        updatedAt: Date.now(),
      };
    });
    setShowSettings(false);
  };

  const resetAll = () => {
    if (!state) return;
    setRunning(false);
    setPast([]);
    setFuture([]);
    setState(emptyState(state.squad, state.settings));
    setLogPeriod(1);
    setShowSettings(false);
  };

  // ---------- Rendu ----------
  if (loading || !state) {
    return (
      <section className="min-h-[60vh] flex items-center justify-center text-gray-500">
        {tr("loading")}
      </section>
    );
  }

  const bench = state.squad.filter((p) => !state.onCourt.includes(p.number));
  const onCourtCount = state.onCourt.filter((n) => n != null).length;
  const periodMax = periodDurationMs(state.settings, state.period);
  const progress = periodMax ? 1 - state.clockMs / periodMax : 0;
  const allPeriods = Array.from(
    { length: Math.max(state.settings.periods, state.period) },
    (_, i) => i + 1
  );
  const periodSubs = state.subs.filter((s) => s.period === logPeriod);

  return (
    <section
      className="min-h-[100dvh] w-full bg-gray-50 text-gray-900 dark:bg-gray-950 dark:text-gray-100"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      {/* Header */}
      <div className="sticky top-0 z-40 bg-gray-50/90 dark:bg-gray-950/90 backdrop-blur border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-3 py-2 flex items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-white/80 dark:bg-gray-900/60 active:scale-[0.98]"
            aria-label={tr("actions.back")}
          >
            <ArrowLeft size={18} />
            <span className="hidden sm:inline text-sm font-medium">{tr("actions.back")}</span>
          </button>

          <div className="inline-flex rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-1">
            <button
              onClick={() => setTab("subs")}
              className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg font-medium ${
                tab === "subs" ? "bg-orange-600 text-white" : "text-gray-700 dark:text-gray-200"
              }`}
            >
              <ArrowLeftRight size={16} /> {tr("tabs.subs")}
            </button>
            <button
              onClick={() => setTab("analysis")}
              className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg font-medium ${
                tab === "analysis" ? "bg-orange-600 text-white" : "text-gray-700 dark:text-gray-200"
              }`}
            >
              <BarChart2 size={16} /> {tr("tabs.analysis")}
            </button>
          </div>

          <div className="flex-1 min-w-0 text-center hidden md:block">
            <h1 className="text-base font-bold text-orange-600 dark:text-orange-400 truncate">
              {tr("title")}
              {opponent ? ` · ${tr("vs", { opponent })}` : ""}
            </h1>
          </div>

          <div className="ml-auto flex items-center gap-1">
            <IconBtn onClick={undo} disabled={!past.length} label={tr("actions.undo")}>
              <Undo2 size={18} />
            </IconBtn>
            <IconBtn onClick={redo} disabled={!future.length} label={tr("actions.redo")}>
              <Redo2 size={18} />
            </IconBtn>
            <IconBtn onClick={() => setShowSettings(true)} label={tr("actions.settings")}>
              <Settings2 size={18} />
            </IconBtn>
            <button
              onClick={saveRemote}
              disabled={saveStatus === "saving"}
              className={`inline-flex items-center gap-1 px-3 py-2 rounded-xl text-sm font-semibold active:scale-[0.98] ${
                saveStatus === "error"
                  ? "bg-red-600 text-white"
                  : saveStatus === "saved"
                  ? "bg-green-600 text-white"
                  : "bg-black text-white hover:bg-gray-800 dark:bg-white dark:text-black"
              }`}
            >
              {saveStatus === "saved" ? <Check size={16} /> : <Save size={16} />}
              <span className="hidden sm:inline">
                {saveStatus === "saving"
                  ? tr("save.saving")
                  : saveStatus === "saved"
                  ? tr("save.saved")
                  : saveStatus === "error"
                  ? tr("save.error")
                  : tr("save.label")}
              </span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 pt-4 pb-24">
        {/* Chrono */}
        <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-3 sm:p-4 mb-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex flex-col items-center">
              <div className="flex items-center gap-1">
                <IconBtn onClick={() => changePeriod(-1)} disabled={state.period <= 1} label={tr("period.prev")}>
                  <ChevronLeft size={20} />
                </IconBtn>
                <span className="text-3xl font-bold tabular-nums min-w-[3ch] text-center">
                  {periodLabel(state.period)}
                </span>
                <IconBtn onClick={() => changePeriod(1)} label={tr("period.next")}>
                  <ChevronRight size={20} />
                </IconBtn>
              </div>
              <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                {tr("period.label")}
              </span>
            </div>

            <div className="flex flex-col items-center">
              <div className="flex items-center gap-2">
                <IconBtn onClick={() => adjustClock(1000)} label="+1s">
                  <Plus size={20} />
                </IconBtn>
                {editingClock != null ? (
                  <input
                    autoFocus
                    value={editingClock}
                    onChange={(e) => setEditingClock(e.target.value)}
                    onBlur={commitClockEdit}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") commitClockEdit();
                      if (e.key === "Escape") setEditingClock(null);
                    }}
                    inputMode="numeric"
                    className="w-32 text-center text-4xl font-mono font-bold rounded-xl border border-orange-400 bg-transparent px-2 py-1"
                  />
                ) : (
                  <button
                    onClick={() => !running && setEditingClock(formatClock(state.clockMs))}
                    title={running ? undefined : tr("clock.edit")}
                    className={`text-4xl sm:text-5xl font-mono font-bold tabular-nums px-3 py-1 rounded-xl border ${
                      state.clockMs <= 0
                        ? "border-red-400 text-red-600"
                        : "border-gray-200 dark:border-gray-700"
                    }`}
                  >
                    {formatClock(state.clockMs)}
                  </button>
                )}
                <IconBtn onClick={() => adjustClock(-1000)} label="-1s">
                  <Minus size={20} />
                </IconBtn>
              </div>
              <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                {tr("clock.label")}
              </span>
            </div>

            <button
              onClick={toggleRunning}
              disabled={state.clockMs <= 0}
              aria-label={running ? tr("clock.pause") : tr("clock.start")}
              className={`h-14 w-14 rounded-full flex items-center justify-center text-white shadow active:scale-95 disabled:opacity-40 ${
                running ? "bg-gray-800 dark:bg-gray-700" : "bg-orange-600 hover:bg-orange-700"
              }`}
            >
              {running ? <Pause size={26} /> : <Play size={26} className="ml-1" />}
            </button>
          </div>
          <div className="mt-3 h-1.5 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
            <div className="h-full bg-orange-500 transition-[width]" style={{ width: `${progress * 100}%` }} />
          </div>
        </div>

        {tab === "subs" ? (
          <div className="grid gap-4 lg:grid-cols-[1fr_1fr_minmax(280px,0.9fr)]">
            {/* Sur le terrain */}
            <Card
              title={`${tr("court.title")} (${onCourtCount}/${COURT_SIZE})`}
            >
              {!state.hasStarted && (
                <p className="text-xs text-gray-500 mb-2">{tr("court.setupHint")}</p>
              )}
              <div className="space-y-2">
                {state.onCourt.map((num, slot) => {
                  const selected = selectedOutSlot === slot;
                  const target = selectedIn != null;
                  return (
                    <div
                      key={slot}
                      className="flex items-center gap-2"
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        const n = Number(e.dataTransfer.getData("text/plain"));
                        if (!Number.isNaN(n)) substitute(slot, n);
                      }}
                    >
                      <span className="w-11 shrink-0 text-center text-[11px] font-bold rounded-full border border-gray-300 dark:border-gray-700 py-2">
                        {slotLabels[slot]}
                      </span>
                      <button
                        onClick={() => onCourtClick(slot)}
                        className={`flex-1 min-w-0 flex items-center justify-between gap-2 rounded-xl border px-3 py-2.5 text-left transition ${
                          selected
                            ? "border-red-500 bg-red-50 dark:bg-red-950/40 ring-2 ring-red-300"
                            : target
                            ? "border-dashed border-green-500 bg-green-50/60 dark:bg-green-950/30"
                            : num == null
                            ? "border-dashed border-gray-300 dark:border-gray-700 text-gray-400"
                            : "border-orange-200 dark:border-orange-900 bg-orange-50/50 dark:bg-orange-950/20"
                        }`}
                      >
                        <span className="truncate font-medium">
                          {num == null ? tr("court.empty") : playerLabel(num)}
                        </span>
                        {num != null && (
                          <span className="shrink-0 text-right leading-tight">
                            <span className="block tabular-nums text-sm font-semibold">
                              {formatPlay(totalPlay(state, num))}
                            </span>
                            {state.hasStarted && (
                              <span className="block tabular-nums text-[10px] text-gray-500">
                                {tr("court.stint", { time: formatPlay(state.stintMs[num] || 0) })}
                              </span>
                            )}
                          </span>
                        )}
                      </button>
                      {num != null && (
                        <button
                          onClick={() => removeFromCourt(slot)}
                          aria-label={tr("court.remove")}
                          className="p-2 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40"
                        >
                          <ArrowRight size={18} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              {(selectedOutSlot != null || selectedIn != null) && (
                <div className="mt-3 flex items-center justify-between gap-2 rounded-xl bg-gray-100 dark:bg-gray-800 px-3 py-2 text-sm">
                  <span>
                    {selectedOutSlot != null
                      ? tr("hint.pickIn", { player: playerLabel(state.onCourt[selectedOutSlot]) })
                      : tr("hint.pickOut", { player: playerLabel(selectedIn) })}
                  </span>
                  <button onClick={clearSelection} className="p-1" aria-label={tr("actions.cancel")}>
                    <X size={16} />
                  </button>
                </div>
              )}
            </Card>

            {/* Banc */}
            <Card
              title={`${tr("bench.title")} (${bench.length})`}
              action={
                <button
                  onClick={() => setShowSquad(true)}
                  className="inline-flex items-center gap-1 rounded-lg border border-orange-300 text-orange-700 dark:text-orange-300 dark:border-orange-800 px-2.5 py-1.5 text-xs font-semibold"
                >
                  <Users size={14} /> {tr("bench.editSquad")}
                </button>
              }
            >
              {state.squad.length === 0 ? (
                <p className="text-sm text-gray-500 italic">{tr("bench.noPlayers")}</p>
              ) : (
                <div className="space-y-2">
                  {bench.map((p) => {
                    const selected = selectedIn === p.number;
                    return (
                      <button
                        key={p.number}
                        draggable
                        onDragStart={(e) => e.dataTransfer.setData("text/plain", String(p.number))}
                        onClick={() => onBenchClick(p.number)}
                        className={`w-full flex items-center justify-between gap-2 rounded-xl border px-3 py-2.5 text-left transition ${
                          selected
                            ? "border-green-500 bg-green-50 dark:bg-green-950/40 ring-2 ring-green-300"
                            : selectedOutSlot != null
                            ? "border-dashed border-green-500 bg-white dark:bg-gray-900"
                            : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                        }`}
                      >
                        <span className="flex items-center gap-2 min-w-0">
                          <ArrowLeft size={16} className="text-green-600 shrink-0" />
                          <span className="truncate font-medium">{playerLabel(p.number)}</span>
                        </span>
                        <span className="tabular-nums text-sm">{formatPlay(totalPlay(state, p.number))}</span>
                      </button>
                    );
                  })}
                  {bench.length === 0 && (
                    <p className="text-sm text-gray-500 italic">{tr("bench.empty")}</p>
                  )}
                </div>
              )}
            </Card>

            {/* 5 de départ + changements */}
            <Card title={tr("starters.title")}>
              {state.hasStarted ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-5">
                  {state.starters.map((num, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <span className="w-10 text-center text-[10px] font-bold rounded-full border border-gray-300 dark:border-gray-700 py-1">
                        {slotLabels[i]}
                      </span>
                      <span className="truncate">{playerLabel(num)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic mb-5">{tr("starters.pending")}</p>
              )}

              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <h3 className="font-bold">{tr("log.title")}</h3>
                <div className="flex flex-wrap gap-1">
                  {allPeriods.map((p) => (
                    <button
                      key={p}
                      onClick={() => setLogPeriod(p)}
                      className={`px-2 py-1 rounded-md text-xs font-semibold border ${
                        logPeriod === p
                          ? "bg-orange-600 border-orange-600 text-white"
                          : "border-gray-200 dark:border-gray-700"
                      }`}
                    >
                      {periodLabel(p)}
                    </button>
                  ))}
                </div>
              </div>
              {periodSubs.length === 0 ? (
                <p className="text-sm text-gray-500 italic">{tr("log.empty")}</p>
              ) : (
                <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                  {[...periodSubs].reverse().map((s) => (
                    <li key={s.id} className="py-2 flex items-start gap-3 text-sm">
                      <span className="font-mono tabular-nums text-gray-500 shrink-0">{formatClock(s.clockMs)}</span>
                      <span className="min-w-0">
                        {s.inNum != null && (
                          <span className="block text-green-700 dark:text-green-400 truncate">
                            ↑ {playerLabel(s.inNum)}
                          </span>
                        )}
                        {s.outNum != null && (
                          <span className="block text-red-600 dark:text-red-400 truncate">
                            ↓ {playerLabel(s.outNum)}
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        ) : (
          <Analysis state={state} periods={allPeriods} periodLabel={periodLabel} playerLabel={playerLabel} tr={tr} />
        )}
      </div>

      {showSquad && (
        <SquadModal
          current={state.squad}
          roster={teamRoster}
          onCourt={state.onCourt}
          onClose={() => setShowSquad(false)}
          onSave={applySquad}
          tr={tr}
        />
      )}

      {showSettings && (
        <SettingsModal
          settings={state.settings}
          onClose={() => setShowSettings(false)}
          onSave={applySettings}
          onReset={resetAll}
          tr={tr}
        />
      )}
    </section>
  );
};

// ---------- Sous-composants ----------

const SettingsModal = ({
  settings,
  onClose,
  onSave,
  onReset,
  tr,
}: {
  settings: Settings;
  onClose: () => void;
  onSave: (s: Settings) => void;
  onReset: () => void;
  tr: TR;
}) => {
  const [draft, setDraft] = useState(settings);
  const [confirmReset, setConfirmReset] = useState(false);

  const field = (key: keyof Settings, label: string, min: number, max: number) => (
    <label className="flex items-center justify-between gap-3 py-2">
      <span className="text-sm">{label}</span>
      <input
        type="number"
        min={min}
        max={max}
        value={draft[key]}
        onChange={(e) => {
          const v = parseInt(e.target.value, 10);
          setDraft((d) => ({ ...d, [key]: Number.isNaN(v) ? min : Math.min(max, Math.max(min, v)) }));
        }}
        className="w-20 rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-2 py-1.5 text-right"
      />
    </label>
  );

  return (
    <Modal
      title={tr("settings.title")}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 text-sm">
            {tr("actions.cancel")}
          </button>
          <button onClick={() => onSave(draft)} className="px-4 py-2 rounded-xl bg-orange-600 text-white text-sm font-semibold">
            {tr("actions.validate")}
          </button>
        </>
      }
    >
      {field("periods", tr("settings.periods"), 1, 8)}
      {field("periodMinutes", tr("settings.periodMinutes"), 1, 30)}
      {field("otMinutes", tr("settings.otMinutes"), 1, 15)}

      <div className="mt-6 rounded-xl border border-red-200 dark:border-red-900 p-3">
        <p className="text-sm font-semibold text-red-600 mb-1">{tr("settings.resetTitle")}</p>
        <p className="text-xs text-gray-500 mb-2">{tr("settings.resetHint")}</p>
        {confirmReset ? (
          <div className="flex gap-2">
            <button onClick={onReset} className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm font-semibold">
              {tr("settings.resetConfirm")}
            </button>
            <button onClick={() => setConfirmReset(false)} className="px-3 py-1.5 rounded-lg border text-sm">
              {tr("actions.cancel")}
            </button>
          </div>
        ) : (
          <button onClick={() => setConfirmReset(true)} className="px-3 py-1.5 rounded-lg border border-red-300 text-red-600 text-sm">
            {tr("settings.reset")}
          </button>
        )}
      </div>
    </Modal>
  );
};

const Analysis = ({
  state,
  periods,
  periodLabel,
  playerLabel,
  tr,
}: {
  state: RotationState;
  periods: number[];
  periodLabel: (p: number) => string;
  playerLabel: (n: number | null) => string;
  tr: TR;
}) => {
  const rows = state.squad
    .map((p) => {
      const byPeriod = state.playMs[p.number] || {};
      const total = Object.values(byPeriod).reduce((a, b) => a + b, 0);
      const entries = state.subs.filter((s) => s.inNum === p.number).length +
        (state.starters.includes(p.number) ? 1 : 0);
      return { p, byPeriod, total, entries };
    })
    .sort((a, b) => b.total - a.total);

  const max = Math.max(1, ...rows.map((r) => r.total));
  const onCourt = new Set(state.onCourt);

  return (
    <Card title={tr("analysis.title")}>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500 italic">{tr("bench.noPlayers")}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-gray-500 border-b border-gray-200 dark:border-gray-800">
                <th className="py-2 pr-3">{tr("analysis.player")}</th>
                <th className="py-2 pr-3 min-w-[160px]">{tr("analysis.total")}</th>
                {periods.map((p) => (
                  <th key={p} className="py-2 px-2 text-right">{periodLabel(p)}</th>
                ))}
                <th className="py-2 pl-2 text-right">{tr("analysis.entries")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ p, byPeriod, total, entries }) => (
                <tr key={p.number} className="border-b border-gray-100 dark:border-gray-800/60">
                  <td className="py-2 pr-3 whitespace-nowrap">
                    {onCourt.has(p.number) && (
                      <span className="inline-block w-2 h-2 rounded-full bg-orange-500 mr-2 align-middle" />
                    )}
                    {playerLabel(p.number)}
                  </td>
                  <td className="py-2 pr-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
                        <div className="h-full bg-orange-500" style={{ width: `${(total / max) * 100}%` }} />
                      </div>
                      <span className="tabular-nums font-semibold w-12 text-right">{formatPlay(total)}</span>
                    </div>
                  </td>
                  {periods.map((per) => (
                    <td key={per} className="py-2 px-2 text-right tabular-nums text-gray-600 dark:text-gray-300">
                      {byPeriod[per] ? formatPlay(byPeriod[per]) : "–"}
                    </td>
                  ))}
                  <td className="py-2 pl-2 text-right tabular-nums">{entries}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

export default RotationManager;
